import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ServicesType } from "@/domain/models/job";
import { UpdateServicePropsType } from "@/src/domain/service";

import { ServiceApi } from "../api/ServiceApi";
import { ServiceQueryKeys } from "../queries/serviceQueries";
import { useTasksToDumpQuery } from "../queries/taskQueries";

export const useUpdateServiceMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    UpdateServicePropsType,
    unknown,
    UpdateServicePropsType
  >({
    mutationFn: params => ServiceApi.updateService(params),
    onSuccess: updatedService => {
      const { jobId, serviceId } = updatedService;

      if (!jobId || !serviceId) {
        return;
      }

      queryClient.setQueryData(
        ServiceQueryKeys.details({ jobId, serviceId }),
        (oldData: ServicesType | undefined) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            ...updatedService,
          };
        },
      );
    },
  });

  return mutation;
};

export const useUpdateAllDriverDumpServicesMutation = () => {
  const queryClient = useQueryClient();

  const { data: tasksToDump } = useTasksToDumpQuery();

  const servicesToDumpRefs = tasksToDump?.map(task => ServiceApi.getRef(task));

  const mutation = useMutation({
    mutationFn: (serviceParams: Partial<ServicesType>) =>
      ServiceApi.updateServicesInTransaction({
        servicesToUpdate: servicesToDumpRefs ?? [],
        serviceParams,
      }),
    onSuccess: result => {
      const { updatedServices, serviceParams } = result;

      const promises = updatedServices.map(async updatedService => {
        const updatedServiceData =
          await ServiceApi.getServiceByRef(updatedService);

        if (!updatedServiceData) {
          return;
        }

        const { jobId, id } = updatedServiceData;

        queryClient.setQueryData(
          ServiceQueryKeys.details({ jobId, serviceId: id }),
          (oldData: ServicesType | undefined) => {
            if (!oldData) {
              return oldData;
            }

            return {
              ...oldData,
              ...serviceParams,
            };
          },
        );
      });

      return Promise.allSettled(promises);
    },
  });

  return mutation;
};
